import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { OrderListItem } from '@/components/OrderListItem'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { SectionTitle } from '@/components/ui/SectionTitle'
import { publicApi } from '@/lib/api'
import { cachePaidOrders, isLookupVisibleOrder, readCachedPaidOrders, rememberOrderAccess } from '@/lib/orderCache'
import { buildPagination, paginateArray } from '@/lib/pagination'

const ORDER_PAGE_SIZE = 6
type OrdersView = 'cached' | 'query'

export function OrdersQueryPage() {
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [activeView, setActiveView] = useState<OrdersView>('cached')
  const [cachedPage, setCachedPage] = useState(1)
  const [queryPage, setQueryPage] = useState(1)
  const [cachedOrders, setCachedOrders] = useState(() => readCachedPaidOrders())
  const siteSettingsQuery = useQuery({
    queryKey: ['public-site-settings'],
    queryFn: publicApi.getSiteSettings,
  })

  const mutation = useMutation({
    mutationFn: () => publicApi.queryOrders(phone, email),
    onMutate: () => {
      setActiveView('query')
      setQueryPage(1)
    },
  })

  useEffect(() => {
    if (!mutation.data) {
      return
    }

    const visibleOrders = mutation.data.filter(isLookupVisibleOrder)
    rememberOrderAccess(visibleOrders)
    cachePaidOrders(visibleOrders)
    setCachedOrders(readCachedPaidOrders())
  }, [mutation.data])

  const visibleOrders = mutation.data?.filter(isLookupVisibleOrder) ?? []
  const cachedOrdersPage = paginateArray(cachedOrders, cachedPage, ORDER_PAGE_SIZE)
  const queryOrdersPage = paginateArray(visibleOrders, queryPage, ORDER_PAGE_SIZE)

  useEffect(() => {
    if (cachedPage !== cachedOrdersPage.currentPage) {
      setCachedPage(cachedOrdersPage.currentPage)
    }
  }, [cachedOrdersPage.currentPage, cachedPage])

  useEffect(() => {
    if (queryPage !== queryOrdersPage.currentPage) {
      setQueryPage(queryOrdersPage.currentPage)
    }
  }, [queryOrdersPage.currentPage, queryPage])

  useEffect(() => {
    setCachedPage(1)
  }, [cachedOrders.length])

  useEffect(() => {
    setQueryPage(1)
  }, [visibleOrders.length])

  return (
    <div className="page-stack">
      <section className="query-hero">
        <Link className="back-link" to="/">
          <ArrowLeft size={16} />
          返回首页
        </Link>
        <SectionTitle eyebrow="Lookup" title="记录查询" />
        <Card className="query-form">
          <div className="form-grid">
            <label className="field">
              <span>手机号</span>
              <Input onChange={event => setPhone(event.target.value)} placeholder="13800138000" value={phone} />
            </label>
            <label className="field">
              <span>邮箱</span>
              <Input onChange={event => setEmail(event.target.value)} placeholder="you@example.com" value={email} />
            </label>
          </div>
          <Button className="fit-width" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            <Search size={16} />
            {mutation.isPending ? '查询中...' : '开始查询'}
          </Button>
        </Card>
      </section>

      <div className="segment-row">
        <button
          className={`segment-pill ${activeView === 'cached' ? 'is-active' : ''}`}
          onClick={() => setActiveView('cached')}
          type="button"
        >
          浏览器记录
        </button>
        <button
          className={`segment-pill ${activeView === 'query' ? 'is-active' : ''}`}
          onClick={() => setActiveView('query')}
          type="button"
        >
          本次查询结果
        </button>
      </div>

      {activeView === 'cached' ? (
        cachedOrders.length > 0 ? (
          <section className="page-stack">
            <SectionTitle eyebrow="Browser History" title="浏览器记录" />
            <div className="page-stack compact">
              {cachedOrdersPage.items.map(order => (
                <OrderListItem key={order.id} order={order} timeZone={siteSettingsQuery.data?.timeZone} />
              ))}
            </div>
            <Pagination
              currentPage={cachedOrdersPage.currentPage}
              onPageChange={setCachedPage}
              pageItems={buildPagination(cachedOrdersPage.currentPage, cachedOrdersPage.totalPages)}
              summary={`共 ${cachedOrders.length} 条记录，第 ${cachedOrdersPage.currentPage} / ${cachedOrdersPage.totalPages} 页`}
              totalPages={cachedOrdersPage.totalPages}
            />
          </section>
        ) : (
          <Card className="empty-state">当前浏览器还没有已支付订单记录。</Card>
        )
      ) : mutation.data ? (
        visibleOrders.length > 0 ? (
          <section className="page-stack">
            <SectionTitle eyebrow="Current Lookup" title="本次查询结果" />
            <div className="page-stack compact">
              {queryOrdersPage.items.map(order => (
                <OrderListItem key={order.id} order={order} timeZone={siteSettingsQuery.data?.timeZone} />
              ))}
            </div>
            <Pagination
              currentPage={queryOrdersPage.currentPage}
              onPageChange={setQueryPage}
              pageItems={buildPagination(queryOrdersPage.currentPage, queryOrdersPage.totalPages)}
              summary={`共 ${visibleOrders.length} 条结果，第 ${queryOrdersPage.currentPage} / ${queryOrdersPage.totalPages} 页`}
              totalPages={queryOrdersPage.totalPages}
            />
          </section>
        ) : (
          <Card className="empty-state">没有查到待发货或已发货订单，请确认手机号与邮箱是否与下单时一致。</Card>
        )
      ) : null}
    </div>
  )
}
